import React from "react";
import { motion } from "framer-motion";
import { 
  Factory, 
  Settings, 
  Cpu, 
  Droplets, 
  ShieldCheck, 
  ChevronRight, 
  Layers,
  Gauge,
  Wrench
} from "lucide-react";
import { IMAGES } from "@/assets/images";
import { productionLines, coatingFacilities } from "@/data/index";
import { ProductionLineCard, CoatingFacilityCard } from "@/components/Cards";
import { ROUTE_PATHS, scrollToSection } from "@/lib/index";

export default function Production() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.HERO_INDUSTRIAL_2} 
            alt="Üretim Tesisleri" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/40" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Üretim <span className="text-accent-foreground/80">Teknolojileri</span>
            </h1>
            <p className="text-xl max-w-2xl text-slate-200 leading-relaxed">
              Entegre üretim tesislerimizde, hammaddeden nihai ürüne kadar her aşamada 
              endüstriyel hassasiyet ve ileri teknoloji ile hizmet veriyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Production Navigation - Quick Anchors */}
      <nav className="sticky top-24 z-40 bg-white/95 backdrop-blur-md border-b border-border shadow-sm hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex justify-center space-x-8 py-4">
            {[ 
              { id: "dokumhane", label: "Dökümhane", icon: <Droplets className="w-4 h-4" /> },
              { id: "kalip-imalati", label: "Kalıp İmalatı", icon: <Wrench className="w-4 h-4" /> },
              { id: "ekstruzyon", label: "Ekstrüzyon", icon: <Factory className="w-4 h-4" /> },
              { id: "yuzey-islem", label: "Yüzey İşlem", icon: <Layers className="w-4 h-4" /> },
              { id: "mekanik", label: "Mekanik İşlem", icon: <Settings className="w-4 h-4" /> }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors cursor-pointer"
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Foundry Section */}
      <section id="dokumhane" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                <Droplets className="w-4 h-4" />
                Hammaddeden Başlayan Kalite
              </div>
              <h2 className="text-4xl font-bold mb-6 text-primary">Dökümhane İşlemleri</h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Tesisimiz bünyesinde bulunan modern dökümhane, üretim zincirimizin ilk ve en kritik halkasıdır. 
                Geri dönüştürülmüş alüminyum ve birincil külçeleri harmanlayarak, uluslararası standartlarda 
                6000 ve 7000 serisi alaşımlı biyetler üretiyoruz.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Yıllık 15.000 Ton Biyet Dökümü",
                  "Hassas Spektrometre Analizi",
                  "Homojenizasyon Fırınları",
                  "Sıcak Kesim Biyet Hazırlama"
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-primary mt-0.5" />
                    <span className="text-slate-700 font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video group"
              >
                <img 
                  src={IMAGES.ALUMINUM_BILLET_CASTING_PLANT_1_302} 
                  alt="Alüminyum Biyet Döküm Tesisi" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold mb-1">Biyet Döküm Tesisi</h3>
                  <p className="text-sm opacity-90">Modern döküm teknolojisi</p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video group"
              >
                <img 
                  src={IMAGES.INDIR_2_303} 
                  alt="Döküm İşlem Süreci" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold mb-1">İşlem Süreci</h3>
                  <p className="text-sm opacity-90">Kalite kontrol aşamaları</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Kalıp İmalatı Section */}
      <section id="kalip-imalati" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                <Wrench className="w-4 h-4" />
                Hassas Kalıp Teknolojisi
              </div>
              <h2 className="text-4xl font-bold mb-6 text-primary">Kalıp İmalatı</h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Yılların deneyimi ile ekstrüzyon kalıplarının tasarımından imalatına kadar tüm süreçleri 
                kendi bünyemizde gerçekleştiriyoruz. CAD/CAM teknolojileri ile hassas kalıp üretimi.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "3D Tasarım ve Simülasyon",
                  "CNC Hassas İmalat",
                  "Kalite Kontrol Sistemleri",
                  "H13 Çelik Kalıp Malzemesi"
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-primary mt-0.5" />
                    <span className="text-slate-700 font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video lg:aspect-square"
            >
              <img src={IMAGES.EKRAN_G_R_NT_S_2026_02_10_172309_276} alt="Kalıp İmalatı" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Extrusion Section */}
      <section id="ekstruzyon" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-6 text-primary">Ekstrüzyon Hatları</h2>
            <p className="text-muted-foreground">
              4 inçten 8 inçe kadar uzanan 5 farklı ekstrüzyon hattımızla, mikro boyutlardan 
              devasa endüstriyel kesitlere kadar her türlü profili yüksek hassasiyetle üretiyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productionLines.map((line) => (
              <ProductionLineCard key={line.id} line={line} />
            ))}
          </div>
        </div>
      </section>

      {/* Coating Section */}
      <section id="yuzey-islem" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-16 items-center mb-16">
            <div className="lg:w-1/2">
              <h2 className="text-4xl font-bold mb-6 text-primary">Yüzey İşlem ve Boyama</h2>
              <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                Alüminyumun estetiğini ve dayanıklılığını koruyan en son teknoloji yüzey işlem tesislerimiz; 
                2 adet yatay ve 1 adet dikey toz boyama hattı ile hizmet vermektedir. 
                Qualicoat ve Seaside standartlarına uygun üretim yapıyoruz.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 rounded-xl bg-accent/50 border border-border">
                  <span className="text-3xl font-bold text-primary block">21.000</span>
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Toplam Ton/Yıl</span>
                </div>
                <div className="p-4 rounded-xl bg-accent/50 border border-border">
                  <span className="text-3xl font-bold text-primary block">RAL</span>
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Tüm Renk Seçenekleri</span>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2 grid grid-cols-1 gap-6 w-full">
              {coatingFacilities.map((facility) => (
                <CoatingFacilityCard key={facility.id} facility={facility} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mechanical Processing */}
      <section id="mekanik" className="py-24 bg-primary text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: <Cpu />, title: "5 Eksen CNC", desc: "Hassas talaşlı imalat" },
                  { icon: <Settings />, title: "Otomatik Kesim", desc: "Hızlı ve toleranslı boylama" },
                  { icon: <Gauge />, title: "Punch & Delik", desc: "Seri üretim çözümleri" },
                  { icon: <ShieldCheck />, title: "Montaj", desc: "Entegre ürün grupları" }
                ].map((item, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20"
                  >
                    <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center mb-4">
                      {item.icon}
                    </div>
                    <h3 className="font-bold mb-1">{item.title}</h3>
                    <p className="text-sm text-white/70">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold mb-6">Mekanik İşlem Merkezi</h2>
              <p className="text-white/80 text-lg mb-8 leading-relaxed">
                Sadece profil üretmekle kalmıyor, müşterilerimizin yarı mamul veya bitmiş ürün 
                ihtiyaçlarını karşılamak üzere gelişmiş bir mekanik işlem parkuru işletiyoruz. 
                CNC işleme merkezlerimiz, hassas kesim makinelerimiz ve uzman ekibimizle projelerinize 
                değer katıyoruz.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="w-2 h-2 rounded-full bg-accent-foreground animate-pulse" />
                  <span>Hassas Tolerans Kontrolü (+/- 0.1mm)</span>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="w-2 h-2 rounded-full bg-accent-foreground animate-pulse" />
                  <span>Yüksek Kapasiteli Seri İmalat</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Standards CTA */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8 text-primary">Kalite Standartlarımız</h2>
          <div className="flex flex-wrap justify-center gap-12">
            {["ISO 9001", "ISO 14001", "Qualicoat", "CE", "TSE"].map((std) => (
              <div key={std} className="flex flex-col items-center opacity-60 hover:opacity-100 transition-opacity">
                <ShieldCheck className="w-12 h-12 text-primary mb-2" />
                <span className="font-bold text-primary">{std}</span>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <button 
              onClick={() => window.location.href = ROUTE_PATHS.CONTACT}
              className="bg-primary text-white px-8 py-4 rounded-lg font-bold hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/20"
            >
              Teklif ve Teknik Destek İste
            </button>
          </div>
        </div>
      </section>


    </div>
  );
}
